import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, Search, Edit, Trash2, CheckCircle, XCircle, ArrowLeft, User, Briefcase, MapPin, Pound, Clock, History, Wifi, WifiOff, MessageSquarePlus, Eye } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

export default function AdminUserManagement() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [trades, setTrades] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [profileFormData, setProfileFormData] = useState({});
  const [loginHistory, setLoginHistory] = useState([]);
  const [historyUser, setHistoryUser] = useState(null);
  const [userActivity, setUserActivity] = useState({});
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsUser, setCreditsUser] = useState(null);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [regionFilter, setRegionFilter] = useState('all'); // all|live|priority|expansion|unmapped

  useEffect(() => {
    checkAuth();
    fetchTrades();
    fetchUserActivity();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [searchQuery, filterType, availabilityFilter, regionFilter, users]);

  // Refresh user activity every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchUserActivity, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUserActivity = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/activity/all`, {
        withCredentials: true
      });
      // Create a map of user_id to activity data
      const activityMap = {};
      response.data.forEach(u => {
        activityMap[u.user_id] = {
          is_currently_online: u.is_currently_online,
          last_seen: u.last_seen
        };
      });
      setUserActivity(activityMap);
    } catch (error) {
      console.error('Failed to fetch user activity');
    }
  };

  const fetchTrades = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/trades`);
      setTrades(response.data);
    } catch (error) {
      console.error('Failed to fetch trades');
    }
  };

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchUsers();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, {
        withCredentials: true
      });
      // Handle paginated response - users are in the 'items' field
      const usersData = response.data.items || response.data;
      setUsers(Array.isArray(usersData) ? usersData : []);
      setFilteredUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      toast.error('Failed to fetch users');
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;
    
    if (filterType !== 'all') {
      filtered = filtered.filter(u => u.user_type === filterType);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Filter by availability mode (for tradespeople)
    if (availabilityFilter) {
      filtered = filtered.filter(u => 
        u.user_type !== 'tradesperson' || 
        (u.profile && u.profile.availability_mode === availabilityFilter)
      );
    }

    // Filter by live-area / region status
    if (regionFilter !== 'all') {
      filtered = filtered.filter(u => {
        const kind = u.region_kind || 'unmapped';
        return regionFilter === kind || (regionFilter === 'unmapped' && !u.region_kind);
      });
    }
    
    setFilteredUsers(filtered);
  };

  const handleEditUser = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}`, {
        withCredentials: true
      });
      setSelectedUser(response.data);
      setEditFormData({
        name: response.data.name,
        email: response.data.email,
        user_type: response.data.user_type,
        email_verified: response.data.email_verified
      });
      setShowEditModal(true);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleSaveUser = async () => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${selectedUser.user_id}`,
        editFormData,
        { withCredentials: true }
      );
      toast.success('User updated successfully');
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  const handleEditProfile = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/profiles/${userId}`, {
        withCredentials: true
      });
      setSelectedUser(response.data);
      setProfileFormData({
        trade: response.data.trade || '',
        qualifications: response.data.qualifications || '',
        work_areas: response.data.work_areas?.join(', ') || '',
        transport: response.data.transport || '',
        daily_rate: response.data.daily_rate || '',
        bio: response.data.bio || '',
        available_dates: response.data.available_dates || []
      });
      setShowProfileModal(true);
    } catch (error) {
      if (error.response?.status === 404) {
        // Profile doesn't exist, create empty form
        const userResponse = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}`, {
          withCredentials: true
        });
        setSelectedUser(userResponse.data);
        setProfileFormData({
          trade: '',
          qualifications: '',
          work_areas: '',
          transport: '',
          daily_rate: '',
          bio: '',
          available_dates: []
        });
        setShowProfileModal(true);
      } else {
        toast.error('Failed to load profile');
      }
    }
  };

  const handleSaveProfile = async () => {
    try {
      const profileData = {
        ...profileFormData,
        work_areas: profileFormData.work_areas.split(',').map(a => a.trim()).filter(a => a),
        daily_rate: parseFloat(profileFormData.daily_rate) || 0
      };
      
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${selectedUser.user_id || selectedUser.user_info?.user_id}/profile`,
        profileData,
        { withCredentials: true }
      );
      toast.success('Profile updated successfully');
      setShowProfileModal(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const handleViewLoginHistory = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users/${userId}/login-history`, {
        withCredentials: true
      });
      setHistoryUser(response.data.user);
      setLoginHistory(response.data.login_history);
      setShowHistoryModal(true);
    } catch (error) {
      toast.error('Failed to fetch login history');
    }
  };

  const handleOpenCreditsModal = (userData) => {
    setCreditsUser(userData);
    setCreditsAmount('');
    setCreditsReason('');
    setShowCreditsModal(true);
  };

  const handleAddCredits = async () => {
    if (!creditsAmount || parseInt(creditsAmount) <= 0) {
      toast.error('Please enter a valid number of credits');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/add-chat-credits`,
        {
          user_id: creditsUser.user_id || creditsUser.user_info?.user_id,
          credits: parseInt(creditsAmount),
          reason: creditsReason || 'Dispute resolution'
        },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setShowCreditsModal(false);
      setCreditsUser(null);
      setCreditsAmount('');
      setCreditsReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add credits');
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    } catch {
      return 'Unknown';
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const handleVerifyEmail = async (userId) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${userId}/verify-email`,
        {},
        { withCredentials: true }
      );
      toast.success('Email verified');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to verify email');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This will delete all their data.`)) {
      return;
    }

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, {
        withCredentials: true
      });
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Dashboard
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">User Management</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Hi, {user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="card mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10"
                data-testid="search-input"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full md:w-48"
              data-testid="filter-select"
            >
              <option value="all">All Users</option>
              <option value="tradesperson">Tradespeople</option>
              <option value="employer">Employers</option>
              <option value="admin">Admins</option>
            </select>
            <select
              value={availabilityFilter || 'all'}
              onChange={(e) => setAvailabilityFilter(e.target.value === 'all' ? null : e.target.value)}
              className="w-full md:w-48"
              data-testid="availability-filter-select"
            >
              <option value="all">All Availability</option>
              <option value="available_now">Available Now</option>
              <option value="available_from">Available From Date</option>
              <option value="custom_dates">Custom Dates</option>
              <option value="busy">Busy / Unavailable</option>
            </select>
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="w-full md:w-48"
              data-testid="region-filter-select"
            >
              <option value="all">All Regions</option>
              <option value="live">Live area</option>
              <option value="priority">Priority</option>
              <option value="expansion">Out of live area / Expansion</option>
              <option value="unmapped">Unknown location</option>
            </select>
          </div>
          <p className="text-sm text-slate-600 mt-2">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </div>

        {/* Users Table */}
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Name</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Email</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Type</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900 hidden md:table-cell">Postcode</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900 hidden lg:table-cell">Town/City</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Region</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900 hidden md:table-cell">Online</th>
                <th className="text-left py-3 px-4 font-semibold text-slate-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <button onClick={() => navigate(`/admin/user/${u.user_id}`)} className="font-medium text-slate-900 hover:text-orange-600 hover:underline text-left" data-testid={`view-name-${u.user_id}`}>
                      {u.name}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <p className="text-slate-600">{u.email}</p>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                      u.user_type === 'admin' ? 'bg-purple-100 text-purple-800' :
                      u.user_type === 'tradesperson' ? 'bg-blue-100 text-blue-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {u.user_type}
                    </span>
                    {/* Show busy indicator for tradespeople */}
                    {u.user_type === 'tradesperson' && u.profile?.availability_mode === 'busy' && (
                      <span className="ml-1 inline-block px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-800">
                        Busy
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell" data-testid={`user-postcode-${u.user_id}`}>
                    <p className="text-slate-700 text-sm font-mono">{u.postcode || <span className="text-slate-300">—</span>}</p>
                  </td>
                  <td className="py-3 px-4 hidden lg:table-cell" data-testid={`user-town-${u.user_id}`}>
                    <p className="text-slate-700 text-sm">{u.town_city || u.city || <span className="text-slate-300">—</span>}</p>
                  </td>
                  <td className="py-3 px-4" data-testid={`user-region-${u.user_id}`}>
                    {(() => {
                      const kind = u.region_kind || 'unmapped';
                      const map = {
                        live: { cls: 'bg-emerald-100 text-emerald-800', label: 'Live area' },
                        priority: { cls: 'bg-amber-100 text-amber-800', label: 'Priority' },
                        expansion: { cls: 'bg-orange-100 text-orange-800', label: 'Out of live area' },
                        unmapped: { cls: 'bg-slate-100 text-slate-700', label: 'Unknown' },
                      };
                      const cfg = map[kind] || map.unmapped;
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium w-fit ${cfg.cls}`}>{cfg.label}</span>
                          {u.mapped_region_name && <span className="text-xs text-slate-500">{u.mapped_region_name}</span>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="py-3 px-4">
                    {u.email_verified ? (
                      <span className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle className="h-4 w-4" />
                        Verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600 text-sm">
                        <XCircle className="h-4 w-4" />
                        Unverified
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 hidden md:table-cell">
                    <div className="flex items-center gap-2">
                      {userActivity[u.user_id]?.is_currently_online ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm" title="Online now">
                          <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                          </span>
                          Online
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-500 text-sm" title={userActivity[u.user_id]?.last_seen ? `Last seen: ${formatDateTime(userActivity[u.user_id].last_seen)}` : 'Never logged in'}>
                          <span className="inline-flex rounded-full h-2.5 w-2.5 bg-slate-300"></span>
                          {formatTimeAgo(userActivity[u.user_id]?.last_seen)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/admin/user/${u.user_id}`)}
                        className="text-slate-600 hover:text-orange-600"
                        title="View Profile"
                        data-testid={`view-btn-${u.user_id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditUser(u.user_id)}
                        className="text-blue-600 hover:text-blue-700"
                        title="Edit Account"
                        data-testid={`edit-btn-${u.user_id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {u.user_type === 'tradesperson' && (
                        <button
                          onClick={() => handleEditProfile(u.user_id)}
                          className="text-orange-600 hover:text-orange-700"
                          title="Edit Profile"
                          data-testid={`profile-btn-${u.user_id}`}
                        >
                          <Briefcase className="h-4 w-4" />
                        </button>
                      )}
                      {u.user_type === 'employer' && (
                        <button
                          onClick={() => handleOpenCreditsModal(u)}
                          className="text-orange-600 hover:text-orange-700"
                          title="Add Chat Credits"
                          data-testid={`credits-btn-${u.user_id}`}
                        >
                          <MessageSquarePlus className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleViewLoginHistory(u.user_id)}
                        className="text-purple-600 hover:text-purple-700"
                        title="View Login History"
                        data-testid={`history-btn-${u.user_id}`}
                      >
                        <History className="h-4 w-4" />
                      </button>
                      {!u.email_verified && (
                        <button
                          onClick={() => handleVerifyEmail(u.user_id)}
                          className="text-green-600 hover:text-green-700"
                          title="Verify Email"
                          data-testid={`verify-btn-${u.user_id}`}
                        >
                          <CheckCircle className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteUser(u.user_id, u.name)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete User"
                        data-testid={`delete-btn-${u.user_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-slate-600">
              No users found
            </div>
          )}
        </div>
      </div>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
              <input
                type="text"
                value={editFormData.name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                className="w-full"
                data-testid="edit-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                value={editFormData.email || ''}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                className="w-full"
                data-testid="edit-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">User Type</label>
              <select
                value={editFormData.user_type || ''}
                onChange={(e) => setEditFormData({ ...editFormData, user_type: e.target.value })}
                className="w-full"
                data-testid="edit-usertype-select"
              >
                <option value="tradesperson">Tradesperson</option>
                <option value="employer">Employer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="email_verified"
                checked={editFormData.email_verified || false}
                onChange={(e) => setEditFormData({ ...editFormData, email_verified: e.target.checked })}
                data-testid="edit-verified-checkbox"
              />
              <label htmlFor="email_verified" className="text-sm text-slate-700">Email Verified</label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveUser}
                className="btn-primary"
                data-testid="save-user-btn"
              >
                Save Changes
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-secondary"
                data-testid="cancel-edit-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-orange-500" />
              Edit Tradesperson Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-slate-600">
                <strong>User:</strong> {selectedUser?.user_info?.name || selectedUser?.name} ({selectedUser?.user_info?.email || selectedUser?.email})
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Trade</label>
                <select
                  value={profileFormData.trade || ''}
                  onChange={(e) => setProfileFormData({ ...profileFormData, trade: e.target.value })}
                  className="w-full"
                  data-testid="profile-trade-select"
                >
                  <option value="">Select Trade</option>
                  {trades.map((t) => (
                    <option key={t.name} value={t.name}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Daily Rate (£)</label>
                <input
                  type="number"
                  value={profileFormData.daily_rate || ''}
                  onChange={(e) => setProfileFormData({ ...profileFormData, daily_rate: e.target.value })}
                  className="w-full"
                  placeholder="e.g., 150"
                  data-testid="profile-rate-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Qualifications</label>
              <textarea
                value={profileFormData.qualifications || ''}
                onChange={(e) => setProfileFormData({ ...profileFormData, qualifications: e.target.value })}
                className="w-full"
                rows={2}
                placeholder="List qualifications, certifications, experience..."
                data-testid="profile-qualifications-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Work Areas (comma separated)</label>
              <input
                type="text"
                value={profileFormData.work_areas || ''}
                onChange={(e) => setProfileFormData({ ...profileFormData, work_areas: e.target.value })}
                className="w-full"
                placeholder="e.g., London, Manchester, Birmingham"
                data-testid="profile-areas-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Transport</label>
              <select
                value={profileFormData.transport || ''}
                onChange={(e) => setProfileFormData({ ...profileFormData, transport: e.target.value })}
                className="w-full"
                data-testid="profile-transport-select"
              >
                <option value="">Select Transport</option>
                <option value="own_vehicle">Own Vehicle</option>
                <option value="public_transport">Public Transport</option>
                <option value="both">Both</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bio</label>
              <textarea
                value={profileFormData.bio || ''}
                onChange={(e) => setProfileFormData({ ...profileFormData, bio: e.target.value })}
                className="w-full"
                rows={3}
                placeholder="Brief description about the tradesperson..."
                data-testid="profile-bio-input"
              />
            </div>

            {selectedUser?.credentials && selectedUser.credentials.length > 0 && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-slate-900 mb-2">Credentials</h4>
                <div className="space-y-2">
                  {selectedUser.credentials.map((cred, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                      <span className="text-sm">{cred.credential_type}: {cred.credential_number}</span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        cred.verified ? 'bg-green-100 text-green-800' : 
                        cred.verification_status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {cred.verified ? 'Verified' : cred.verification_status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-4 border-t">
              <button
                onClick={handleSaveProfile}
                className="btn-primary"
                data-testid="save-profile-btn"
              >
                Save Profile
              </button>
              <button
                onClick={() => setShowProfileModal(false)}
                className="btn-secondary"
                data-testid="cancel-profile-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-500" />
              Login History
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {historyUser && (
              <div className="bg-slate-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-slate-600">
                  <strong>User:</strong> {historyUser.name} ({historyUser.email})
                </p>
              </div>
            )}
            
            <div className="max-h-[400px] overflow-y-auto">
              {loginHistory.length > 0 ? (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-white">
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-3 font-medium text-slate-700">Date & Time</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-700">IP Address</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-700">Device</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginHistory.map((entry, idx) => (
                      <tr key={entry.login_id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-2 px-3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3.5 w-3.5 text-slate-400" />
                            {formatDateTime(entry.login_at)}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-slate-600 font-mono text-xs">
                          {entry.ip_address || 'Unknown'}
                        </td>
                        <td className="py-2 px-3 text-slate-600 text-xs truncate max-w-[200px]" title={entry.user_agent}>
                          {entry.user_agent?.substring(0, 50) || 'Unknown'}
                          {entry.user_agent?.length > 50 && '...'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <History className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>No login history found</p>
                  <p className="text-xs mt-1">This user hasn't logged in yet</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end mt-4 pt-4 border-t">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="btn-secondary"
                data-testid="close-history-btn"
              >
                Close
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Chat Credits Modal */}
      <Dialog open={showCreditsModal} onOpenChange={setShowCreditsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-orange-500" />
              Add Chat Credits
            </DialogTitle>
          </DialogHeader>
          
          {creditsUser && (
            <div className="space-y-4" data-testid="credits-modal">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-medium text-slate-900">{creditsUser.name}</p>
                <p className="text-sm text-slate-600">{creditsUser.email}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Number of Credits to Add *
                </label>
                <input
                  type="number"
                  value={creditsAmount}
                  onChange={(e) => setCreditsAmount(e.target.value)}
                  placeholder="e.g., 5"
                  min="1"
                  className="w-full"
                  data-testid="credits-amount-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Credits are added to the top-up balance (don't expire monthly)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reason (for audit log)
                </label>
                <textarea
                  value={creditsReason}
                  onChange={(e) => setCreditsReason(e.target.value)}
                  placeholder="e.g., Dispute resolution - refund for technical issue"
                  rows={2}
                  className="w-full"
                  data-testid="credits-reason-input"
                />
              </div>
              
              <div className="flex gap-2 pt-4 border-t">
                <button
                  onClick={handleAddCredits}
                  className="btn-primary flex-1"
                  data-testid="submit-credits-btn"
                >
                  Add {creditsAmount || 0} Credits
                </button>
                <button
                  onClick={() => setShowCreditsModal(false)}
                  className="btn-secondary"
                  data-testid="cancel-credits-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
